import { useEffect, useState } from 'react';
import { useCustomMutation, useUpdate, useTranslate } from '@refinedev/core';
import { SaveButton } from '@refinedev/antd';
import { Card, Flex, Form, Input, Space, Tag, Typography } from 'antd';
import type { CategoryUpdateIn } from '@/api/generated/schemas';
import { OrderedPublicationCardsField } from '@/components/user_catalog/OrderedPublicationCardsField';
import { heroRowsToPublicationIds, type HeroLineupRow } from '../heroLineup';
import { heroDraftFromForm, type HeroDraft } from '../homeDraft';
import { heroAudioValidator } from './heroAudioValidator';

export const HERO_URL = 'user_catalog/curated_surfaces/hero';

/** Hero copy lives on the root category; the lineup lives on the curated
 * surface — one save writes both. */
type HeroForm = Pick<CategoryUpdateIn, 'name' | 'description'> & {
  slots: HeroLineupRow[];
};

interface HeroTabProps {
  rootId: number;
  /** Server state; seeds the form once, on mount. */
  initial: HeroForm;
  /** Lineup version last read; the PUT rejects with 409 if it moved on. */
  version: number;
  dirty: boolean;
  onDraftChange: (draft: HeroDraft) => void;
  onLineupSaved: () => void;
}

export const HeroTab = ({
  rootId,
  initial,
  version,
  dirty,
  onDraftChange,
  onLineupSaved,
}: HeroTabProps) => {
  const t = useTranslate();
  const [form] = Form.useForm<HeroForm>();
  const [saving, setSaving] = useState(false);
  const { mutateAsync: replaceLineup } = useCustomMutation();
  const { mutateAsync: patchCategory } = useUpdate();

  const values = Form.useWatch<HeroForm | undefined>([], form);
  // Undefined until the store initialises — reporting it would blank the
  // shell's draft.
  useEffect(() => {
    if (values) onDraftChange(heroDraftFromForm(values));
  }, [values, onDraftChange]);

  // Watched, not the server copy, so the count follows an unsaved edit.
  const publicationIds = heroRowsToPublicationIds(
    values?.slots ?? initial.slots
  );

  const handleFinish = async (formValues: HeroForm) => {
    setSaving(true);
    try {
      await replaceLineup({
        url: HERO_URL,
        method: 'put',
        values: {
          publication_ids: heroRowsToPublicationIds(formValues.slots),
          version,
        },
        successNotification: false,
      });
      await patchCategory({
        resource: 'user_catalog.categories',
        id: rootId,
        values: {
          name: formValues.name,
          description: formValues.description,
        },
        meta: { method: 'patch' },
      });
      onLineupSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      form={form}
      initialValues={initial}
      onFinish={handleFinish}
      layout="vertical"
    >
      <Flex vertical gap="middle">
        <Card size="small" title={t('user_catalog.home_page.cards.content')}>
          <Form.Item
            name="name"
            label={t('user_catalog.home_page.fields.title')}
            extra={t('user_catalog.home_page.fields.title_hint')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('user_catalog.home_page.fields.description')}
            extra={t('user_catalog.home_page.fields.description_hint')}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Card>

        <Card
          size="small"
          title={t('user_catalog.home_page.fields.lineup')}
          extra={<Tag>{publicationIds.length}</Tag>}
        >
          <Flex vertical gap="small">
            <Typography.Text type="secondary">
              {t('user_catalog.home_page.lineup_hint')}
            </Typography.Text>
            {/* The listen-only CTA is this screen's rule; it stands beside
                the built-in deleted / disabled checks, and is advisory —
                a flagged row still saves. */}
            <OrderedPublicationCardsField
              name="slots"
              existingPublicationIds={heroRowsToPublicationIds(initial.slots)}
              validators={[heroAudioValidator]}
            />
          </Flex>
        </Card>

        <Space>
          <SaveButton htmlType="submit" loading={saving} />
          {dirty && (
            <Tag color="warning">{t('user_catalog.home_page.unsaved')}</Tag>
          )}
        </Space>
      </Flex>
    </Form>
  );
};
